import React, { useState, useMemo, useEffect } from 'react';
import Truncate from 'react-truncate';
import { useTranslation } from 'react-i18next';
import DumpSplashScreen from '../../dumpComponent/dumpSplashScreen';
import { withLinkStore, ACTION_TYPES_IS_SKIP_SPLASH_SCREEN, ACTION_TYPES_UPDATE_DEFAULT_PRIVACY_URL, ACTION_TYPES_IS_STRICTLY_NECESSARY_COOKIES } from '../../app/reducers';
import { isMobile } from '../../commonUtil/responsiveUtil';
import { getContentTypeMapping, shouldUseDefaultMessage, isNoTrackView, isShowSignature } from '../../commonUtil/util';
import CustomFooter from '../customFooter';
import { getContentNames } from './splashScreenUtili';
import { getLinkPwdCookie, setGDPRCookie, setUniqueInteractionCookie, FUNCTIONAL_COOKIE_AGE, STRICTLY_NECESSARY_COOKIE_AGE } from '../../commonUtil/cookieUtil';
import { hasPasswordInCookie, isGDPROn } from '../../commonUtil/gdprUtil';
import { PasswordSource, TAG_A_PATTERN, DEFAULT_EN_DISCLAIMER, ConsentGDPRCookieValue, SPECIAL_LANG_HANDLE_LIST } from '../../commonUtil/constants';
import PasswordValidator from '../../components/common/passwordValidator';
import MyPrivacySettings from '../myPrivacySettings';
import { getLang } from '../../commonUtil/i18n';

const SplashScreenContainer = (props) => {
    const { t } = useTranslation();
    const { linkDispatch } = props;
    const { manifests, viewerSetting, gdprSetting, linkBasicInfo, sender, contents, viewerLocation, hasCustomFooter, isLoadingManifest, isOnlyFunctionalCookies, isStrictlyNecessaryCookies } = props.linkStore;
    const { sentAt, isPasswordRequired, isExpired } = linkBasicInfo;
    const { isShowDisclaimerMsg, isShowPrivacySettingBtn, isNoDisclaimer, disclaimerMessage, isEnableGlobalGDPR, privacyLink, isShowRejectAllBtn } = gdprSetting;
    const [isOpenPrivacySetting, setIsOpenPrivacySetting] = useState(false);
    const [isValidPassword, setIsValidPassword] = useState(false);
    const passwordCondition = (isPasswordRequired && isValidPassword) || !isPasswordRequired;
    const lang = getLang();


    const showSharedContentDescription = passwordCondition;
    const sharedContentDescription = useMemo(() => (
        <div className="file-names">
            <Truncate lines={isMobile() ? 3 : 2}>
                {getContentNames(contents, t)}
            </Truncate>
        </div>
    ), [contents]);

    let viewContentType = '';

    if (SPECIAL_LANG_HANDLE_LIST.includes(lang)) {
        viewContentType = getContentTypeMapping(t, contents, false);
    } else {
        viewContentType = getContentTypeMapping(t, contents, false).toLowerCase();
    }

    const useDefaultMessage = gdprSetting.useDefaultMessage || shouldUseDefaultMessage(gdprSetting);
    const replaceDisclaimerMessage = (message, type) => {
        const needReplaceStrList = ['[[content type]]', '{{content type}}', '((content type))', '<<content type>>'];
        const needReplaceStr = needReplaceStrList.find(str => message.includes(str) === true);
        return message ? message.replace(needReplaceStr, type) : '';
    };

    const disclaimerMsg = useMemo(
        () => {
            let footerDisclaimerMessage = '';
            if (useDefaultMessage) {
                // NOTE: please do NOT delete the extra space before each line
                const defaultDisclaimerWithPrivacySetting = `${t('LIVE_SEND_VIEWER_DEFAULT_GDPR_MESSAGE')}
                 ${t('LIVE_SEND_VIEWER_DEFAULT_PRIVACY_SETTINGS')}
                 ${t('LIVE_SEND_VIEWER_DEFAULT_VISIT_PRIVACY_POLICY')}`;

                const defaultDisclaimerWithoutPrivacySetting = `${t('LIVE_SEND_VIEWER_DEFAULT_GDPR_MESSAGE')}
                ${t('LIVE_SEND_VIEWER_DEFAULT_VISIT_PRIVACY_POLICY')}`;

                footerDisclaimerMessage = isShowPrivacySettingBtn ? defaultDisclaimerWithPrivacySetting : defaultDisclaimerWithoutPrivacySetting;
            } else {
                footerDisclaimerMessage = replaceDisclaimerMessage(disclaimerMessage, viewContentType);
                if (TAG_A_PATTERN.test(footerDisclaimerMessage)) {
                    footerDisclaimerMessage = footerDisclaimerMessage.replace(/<a/ig, `<a style='color:${viewerSetting.brandingHilightColor}'`);
                }
            }

            if (gdprSetting.isEnableGlobalGDPR && !disclaimerMessage) {
                footerDisclaimerMessage = isShowPrivacySettingBtn ? DEFAULT_EN_DISCLAIMER.WITH_PRIVACY_SETTING : DEFAULT_EN_DISCLAIMER.WITHOUT_PRIVACY_SETTING;
            }

            return footerDisclaimerMessage ? (<p className="custom-disclamier" dangerouslySetInnerHTML={{ __html: footerDisclaimerMessage }} />) : '';
        }, [viewerSetting.brandingHilightColor]
    );

    useEffect(() => {
        if (isPasswordRequired && getLinkPwdCookie() && manifests.length) {
            setIsValidPassword(true);
            if (isNoTrackView()) {
                linkDispatch({ type: ACTION_TYPES_IS_SKIP_SPLASH_SCREEN, payload: true });
            }
        }
    }, [manifests]);

    const openMyPrivacySetting = () => {
        setIsOpenPrivacySetting(true);
    };

    const isShow4Signature = isShowSignature();
    const getHeaderInfo = () => {
        const contentCount = contents.length;
        if (!isShow4Signature) {
            return contentCount === 1 ? t('LIVE_SEND_VIEWER_SIGNATURE_SINGLE_FILE') :
                t('LIVE_SEND_VIEWER_SIGNATURE_MULTIPLE_FILES', { number: contentCount });
        }

        return contentCount === 1
            ? t('LIVE_SEND_VIEWER_SHARED_A_CONTENT_WITH_YOU', {
                senderName: sender.name,
                contentType: getContentTypeMapping(t, contents)
            })
            : t('LIVE_SEND_VIEWER_SHARED_FILES_WITH_YOU', { senderName: sender.name, contentCount });
    };

    if (hasPasswordInCookie(isPasswordRequired, isExpired) && isLoadingManifest) {
        return <div className="engagement-viewer-busy-spinner" />;
    }

    const splashScreenClassName = isPasswordRequired && isValidPassword ? 'engagement-viewer-fade-in' : '';
    const hilightColor = viewerSetting.brandingHilightColor;
    const viewerLogo = viewerSetting.logo;

    const gdprOn = isGDPROn(gdprSetting, viewerLocation);
    const onPasswordValidate = (isValid) => {
        setIsValidPassword(isValid);
        setUniqueInteractionCookie();

        if ((!gdprOn || isNoTrackView()) && isValid) {
            linkDispatch({ type: ACTION_TYPES_IS_SKIP_SPLASH_SCREEN, payload: true });
        }

        if (isEnableGlobalGDPR && isNoDisclaimer && isValid) {
            linkDispatch({ type: ACTION_TYPES_IS_SKIP_SPLASH_SCREEN, payload: true });
        }
    };

    const setIsStrictlyNecessaryCookies = (value) => {
        linkDispatch({
            type: ACTION_TYPES_IS_STRICTLY_NECESSARY_COOKIES,
            payload: value
        });
    };

    const onClickRejectAll = () => {
        if (gdprOn) {
            setIsStrictlyNecessaryCookies(true);
            setGDPRCookie(ConsentGDPRCookieValue.STRICTLY_NECESSARY_COOKIES, STRICTLY_NECESSARY_COOKIE_AGE);
        }

        setUniqueInteractionCookie();
        linkDispatch({ type: ACTION_TYPES_IS_SKIP_SPLASH_SCREEN, payload: true });
    };

    const showSplashScreenPasswordDescription = isPasswordRequired && !isValidPassword && !isExpired;
    const splashScreenPasswordDescription = t('LIVE_SEND_VIEWER_THIS_CONTENT_REQUIRES_A_PASSWORD');
    let validatorComponent = null;
    if (isPasswordRequired && !isValidPassword && !isExpired) {
        validatorComponent = (<PasswordValidator
            setPasswordValidation={onPasswordValidate}
            source={PasswordSource.LINK_PASSWORD} />);
    }

    const myPrivacySettingsComponent = (<MyPrivacySettings
        isOpen={isOpenPrivacySetting}
        setIsOpen={setIsOpenPrivacySetting}
        isShowStrictlyNecessaryCookies={isShowRejectAllBtn} />);

    const customFooterComponent = hasCustomFooter ? <CustomFooter /> : null;
    const showAvartar = !sender.isAnonymousSender && isShow4Signature;
    const userAvartar = (sender && sender.avatar) || '';
    const userName = (sender && sender.name) || '';

    const showDisclaimerMessage = isShowDisclaimerMsg && passwordCondition && !isExpired;
    const privacyPolicyText = t('LIVE_SEND_PRIVACY_POLICY');
    const readMoreText = t('LIVE_SEND_VIEWER_SEE_MORE');
    const readLessText = t('LIVE_SEND_VIEWER_SEE_LESS');
    const continueViewText = isShowDisclaimerMsg ?
        t('LIVE_SEND_VIEWER_ACCEPT_AND_VIEW_CONTENT_TYPE', { content: getContentTypeMapping(t, contents, false).toLowerCase() }) :
        t('LIVE_SEND_VIEWER_VIEW_CONTENT_TYPE', { content: getContentTypeMapping(t, contents, false).toLowerCase() });

    const onContinueView = () => {
        if (gdprOn) {
            if (isOnlyFunctionalCookies) {
                setGDPRCookie(ConsentGDPRCookieValue.ONLY_FUNCTIONAL_COOKIES, FUNCTIONAL_COOKIE_AGE);
            } else if (isStrictlyNecessaryCookies) {
                setGDPRCookie(ConsentGDPRCookieValue.STRICTLY_NECESSARY_COOKIES, STRICTLY_NECESSARY_COOKIE_AGE);
            } else {
                setGDPRCookie();
            }
        }

        setUniqueInteractionCookie();
        linkDispatch({ type: ACTION_TYPES_IS_SKIP_SPLASH_SCREEN, payload: true });
    };

    const updateSelectPrivacyLink = (result) => {
        linkDispatch({ type: ACTION_TYPES_UPDATE_DEFAULT_PRIVACY_URL, payload: result });
    };

    const privacySettingBtnText = t('LIVE_SEND_VIEWER_MY_PRIVACY_SETTINGS_TITLE');
    const rejectAllBtnText = t('LIVE_SEND_VIEWER_REJECT_ALL_TITLE');

    return (
        <DumpSplashScreen
            className={splashScreenClassName}
            hilightColor={hilightColor}
            viewerLogo={viewerLogo}
            sentAt={sentAt}
            showSharedContentDescription={showSharedContentDescription}
            sharedContentDescription={sharedContentDescription}
            headerHTML={getHeaderInfo()}
            showSplashScreenPasswordDescription={showSplashScreenPasswordDescription}
            splashScreenPasswordDescription={splashScreenPasswordDescription}
            validatorComponent={validatorComponent}
            myPrivacySettingsComponent={myPrivacySettingsComponent}
            customFooterComponent={customFooterComponent}
            showAvartar={showAvartar}
            userAvartarClassName={'avatar'}
            userAvartar={userAvartar}
            userName={userName}
            showDisclaimerMessage={showDisclaimerMessage}
            disclaimerMsg={disclaimerMsg}
            privacyLinkUrl={privacyLink}
            privacyPolicyText={privacyPolicyText}
            readMoreText={readMoreText}
            readLessText={readLessText}
            continueViewText={continueViewText}
            onContinueView={onContinueView}
            showPrivacySettingBtn={isShowPrivacySettingBtn}
            privacySettingBtnText={privacySettingBtnText}
            isNoDisclaimer={isNoDisclaimer}
            updateSelectLink={updateSelectPrivacyLink}
            onClickPrivacySetting={openMyPrivacySetting}
            rejectAllBtnText={rejectAllBtnText}
            showRejectAllBtn={isShowRejectAllBtn}
            onClickRejectAll={onClickRejectAll} />
    );
};

export default withLinkStore(SplashScreenContainer);

